import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import {
  UserRole,
  CommunicationLanguage,
  RegistrationType,
  MatchInstitutionRequest,
} from '../types/auth';
import type { InstitutionDto } from '../types/institution';
import { getInstitutionDisplayName } from '../types/institution';
import { authService } from '../services/auth';
import { getApiErrorMessage } from '../utils/apiErrors';
import FormField from '../components/common/FormField';
import PasswordVisibilityToggle from '../components/common/PasswordVisibilityToggle';
import { Stepper, StepState } from '../design-system/components/Stepper';
import { Button } from '../design-system/components/Button';
import { Card } from '../design-system/components/Card';
import { Heading, Text } from '../design-system/components/Typography';
import { FormSection } from '../design-system/patterns/FormSection';
import {
  Form,
  CheckboxContainer,
  Checkbox,
  CheckboxLabel,
  FormButtonGroup,
} from '../design-system/patterns/FormLayout';
import { Stack } from '../design-system/components/Layout';
import AuthWizardShell from '../components/registration/AuthWizardShell';
import { spacing } from '../design-system/tokens/spacing';
import { typography } from '../design-system/tokens/typography';
import { hexToRgba } from '../utils/colorUtils';

const ErrorMessage = styled(Card)`
  padding: ${spacing[4]};
  background: ${props => hexToRgba(props.theme.colors.error, 0.1)};
  border-left: 4px solid ${props => props.theme.colors.error};
  margin-bottom: ${spacing[6]};
`;

const SuccessMessage = styled(Card)`
  padding: ${spacing[4]};
  background: ${props => hexToRgba(props.theme.colors.success, 0.1)};
  border-left: 4px solid ${props => props.theme.colors.success};
  margin-bottom: ${spacing[6]};
`;

const InstitutionInfoCard = styled(Card)`
  padding: ${spacing[6]};
  margin-bottom: ${spacing[6]};
`;

const BackLink = styled(Link)`
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  font-family: ${typography.fontFamily.primary};
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  margin-bottom: ${spacing[4]};
  transition: color 0.2s ease;
  &:hover {
    color: ${props => props.theme.colors.primaryDark};
    text-decoration: underline;
  }
`;

const CardWithPadding = styled(Card)`
  padding: ${spacing[6]};
`;

const CompactFormButtonGroup = styled(FormButtonGroup)`
  margin-top: ${spacing[4]};
  padding-top: ${spacing[4]};
`;

const HeadingWithMargin = styled(Heading)`
  margin-bottom: ${spacing[2]};
`;

const TextWithMargin = styled(Text)`
  margin-bottom: ${spacing[2]};
`;

const TextUppercase = styled(Text)`
  letter-spacing: 0.05em;
`;

const StepperWrapper = styled.div`
  margin-bottom: ${spacing[4]};
  padding: ${spacing[4]} 0;
`;

const RegisterExistingInstitution: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { register } = useAuth();
  const { success: showSuccess } = useToast();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);

  const [institutionName, setInstitutionName] = useState('');
  const [institutionEmail, setInstitutionEmail] = useState('');

  const [matchedInstitution, setMatchedInstitution] = useState<InstitutionDto | null>(null);
  /** i18n key for lookup/registration errors so language switches re-translate. */
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [communicationLanguage, setCommunicationLanguage] = useState<CommunicationLanguage>(
    CommunicationLanguage.Greek
  );
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [emailVerificationSentTo, setEmailVerificationSentTo] = useState<string | null>(null);

  const preferGr =
    i18n.language?.toLowerCase().startsWith('gr') || i18n.language?.toLowerCase() === 'el';

  useEffect(() => {
    if (errorKey) {
      setError(t(errorKey));
    }
  }, [i18n.language, errorKey, t]);

  const clearLookupResult = () => {
    setMatchedInstitution(null);
    setError(null);
    setErrorKey(null);
  };

  const handleSearchInstitution = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setErrorKey(null);
    setSearching(true);

    try {
      const matchRequest: MatchInstitutionRequest = {
        institutionName: institutionName.trim(),
        institutionEmail: institutionEmail.trim(),
      };

      const response = await authService.matchInstitution(matchRequest);

      if (!response.found) {
        setErrorKey('register.institutionNotFound');
        setError(t('register.institutionNotFound'));
        setMatchedInstitution(null);
        return;
      }

      if (response.hasUserAccount) {
        setErrorKey('register.institutionAlreadyHasAccount');
        setError(t('register.institutionAlreadyHasAccount'));
        setMatchedInstitution(response.institution ?? null);
        return;
      }

      setMatchedInstitution(response.institution ?? null);
      setEmail('');
      setStep(2);
    } catch (err: unknown) {
      const rawMessage = err instanceof Error ? err.message : '';
      setErrorKey(null);
      setError(getApiErrorMessage(rawMessage, t, { registration: true }));
      setMatchedInstitution(null);
    } finally {
      setSearching(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError(t('register.errors.passwordsDoNotMatch'));
      return;
    }

    if (password.length < 6) {
      setError(t('register.errors.passwordMinLength'));
      return;
    }

    if (!acceptedTerms) {
      setError(t('register.errors.acceptTermsRequired'));
      return;
    }

    if (!matchedInstitution) {
      setError(t('register.errors.institutionInfoMissing'));
      return;
    }

    if (!email.trim()) {
      setError(t('register.errors.emailRequired'));
      return;
    }

    setLoading(true);
    setEmailVerificationSentTo(null);
    try {
      const result = await register({
        email: email.trim(),
        password,
        role: UserRole.InstitutionMember,
        communicationLanguage,
        registrationType: RegistrationType.ExistingInstitution,
        existingInstitutionId: matchedInstitution.id,
        institutionName: institutionName.trim(),
        institutionEmail: institutionEmail.trim(),
      });

      // Claim registration always requires email confirmation (no JWT).
      if (result?.requiresEmailConfirmation || (result && !result.token)) {
        const sentTo = result.email ?? email.trim();
        showSuccess(t('register.checkEmailConfirmation'));
        setEmailVerificationSentTo(sentTo);
        return;
      }

      if (result?.token) {
        showSuccess(t('register.registrationSuccessful'));
        navigate('/');
        return;
      }

      setError(t('register.errors.registrationFailed'));
    } catch (err: unknown) {
      const rawMessage = err instanceof Error ? err.message : '';
      setError(getApiErrorMessage(rawMessage, t, { registration: true }));
    } finally {
      setLoading(false);
    }
  };

  const stepperSteps: Array<{ id: string; label: string; state: StepState }> = [
    {
      id: '1',
      label: t('register.institutionSearch'),
      state: step === 1 ? 'active' : step > 1 ? 'completed' : 'pending',
    },
    {
      id: '2',
      label: t('register.institutionVerification'),
      state: step === 2 ? 'active' : step > 2 ? 'completed' : 'pending',
    },
    {
      id: '3',
      label: t('register.accountCreation'),
      state: step === 3 ? 'active' : 'pending',
    },
  ];

  const displayName = matchedInstitution
    ? preferGr
      ? matchedInstitution.institutionName?.gr ||
        matchedInstitution.institutionName?.en ||
        getInstitutionDisplayName(matchedInstitution)
      : matchedInstitution.institutionName?.en ||
        matchedInstitution.institutionName?.gr ||
        getInstitutionDisplayName(matchedInstitution)
    : '';

  const city = preferGr
    ? matchedInstitution?.primaryAddress?.cityGr || matchedInstitution?.primaryAddress?.cityEn
    : matchedInstitution?.primaryAddress?.cityEn || matchedInstitution?.primaryAddress?.cityGr;

  return (
    <AuthWizardShell
      title={t('register.registerExistingInstitution')}
      titleId="register-existing-institution-title"
      backSlot={<BackLink to="/register">← {t('common.back')}</BackLink>}
    >
      <CardWithPadding
        variant="elevated"
        headerVariant="gradient"
        accentBorder="top"
        pattern="dots"
        patternIntensity="subtle"
      >
        <Stack direction="column" spacing="md">
          <StepperWrapper>
            <Stepper steps={stepperSteps} ariaLabel={t('register.progressLabel')} />
          </StepperWrapper>

          {error && (
            <ErrorMessage
              variant="filled"
              accentBorder="left"
              role="alert"
              aria-live="assertive"
            >
              <Text size="sm" color="error">
                {error}
              </Text>
            </ErrorMessage>
          )}

          {step === 1 && (
            <Form onSubmit={handleSearchInstitution}>
              <FormSection
                title={t('register.institutionSearch')}
                description={t('register.institutionSearchIntro')}
                spacing="sm"
              >
                <Stack direction="column" spacing="sm">
                  <FormField
                    label={t('register.institutionNameGreekOrEnglish')}
                    type="text"
                    name="institutionName"
                    value={institutionName}
                    onChange={e => {
                      setInstitutionName(e.target.value);
                      clearLookupResult();
                    }}
                    placeholder={t('register.institutionNamePlaceholder')}
                    required
                    disabled={searching}
                  />
                  <FormField
                    label={t('register.institutionEmail')}
                    type="email"
                    name="institutionEmail"
                    value={institutionEmail}
                    onChange={e => {
                      setInstitutionEmail(e.target.value);
                      clearLookupResult();
                    }}
                    placeholder={t('register.institutionEmailPlaceholder')}
                    required
                    disabled={searching}
                    helperText={t('register.institutionEmailHint')}
                  />
                </Stack>
              </FormSection>

              <CompactFormButtonGroup direction="row" spacing="md" justify="end">
                <Button
                  type="submit"
                  variant="gradient"
                  glow
                  ripple
                  loading={searching}
                  disabled={!institutionName.trim() || !institutionEmail.trim()}
                >
                  {t('register.searchInstitution')}
                </Button>
              </CompactFormButtonGroup>
            </Form>
          )}

          {step === 2 && matchedInstitution && (
            <div>
              <SuccessMessage variant="filled" accentBorder="left">
                <TextWithMargin size="sm" color="success" weight="semibold">
                  {t('register.institutionFound')}
                </TextWithMargin>
                <Text size="sm" color="success">
                  {t('register.institutionFoundDescription')}
                </Text>
              </SuccessMessage>

              <InstitutionInfoCard variant="filled" accentBorder="left">
                <Stack direction="column" spacing="md">
                  <div>
                    <TextUppercase size="xs" color="secondary" weight="semibold">
                      {t('register.institutionName')}
                    </TextUppercase>
                    <HeadingWithMargin variant="h4" color="text">
                      {displayName}
                    </HeadingWithMargin>
                  </div>
                  {matchedInstitution.institutionEmail && (
                    <div>
                      <TextUppercase size="xs" color="secondary" weight="semibold">
                        {t('register.institutionEmail')}
                      </TextUppercase>
                      <Text size="base" color="text">
                        {matchedInstitution.institutionEmail}
                      </Text>
                    </div>
                  )}
                  {city && (
                    <div>
                      <TextUppercase size="xs" color="secondary" weight="semibold">
                        {t('common.city')}
                      </TextUppercase>
                      <Text size="base" color="text">
                        {city}
                      </Text>
                    </div>
                  )}
                </Stack>
              </InstitutionInfoCard>

              <FormButtonGroup direction="row" spacing="md" justify="space-between">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setStep(1);
                    setMatchedInstitution(null);
                    setError(null);
                  }}
                >
                  {t('common.back')}
                </Button>
                <Button type="button" variant="gradient" glow ripple onClick={() => setStep(3)}>
                  {t('common.next')}
                </Button>
              </FormButtonGroup>
            </div>
          )}

          {step === 3 && emailVerificationSentTo && (
            <div>
              <SuccessMessage variant="filled" accentBorder="left">
                <TextWithMargin size="sm" color="success" weight="semibold">
                  {t('register.checkEmailConfirmation')}
                </TextWithMargin>
                <Text size="sm" color="success">
                  {t('register.confirmationEmailSentTo')} {emailVerificationSentTo}
                </Text>
              </SuccessMessage>
              <FormButtonGroup direction="row" spacing="md" justify="start">
                <Button variant="gradient" glow ripple onClick={() => navigate('/login')}>
                  {t('login.signIn')}
                </Button>
              </FormButtonGroup>
            </div>
          )}

          {step === 3 && !emailVerificationSentTo && (
            <Form onSubmit={handleSubmit}>
              <FormSection title={t('register.accountCreation')}>
                <Stack direction="column" spacing="md">
                  <FormField
                    id="existing-institution-email"
                    label={t('common.email')}
                    type="email"
                    name="email"
                    autoComplete="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    disabled={loading}
                    helperText={t('register.enterRealEmail')}
                  />
                  <FormField
                    id="existing-institution-password"
                    label={t('register.password')}
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    disabled={loading}
                    helperText={t('register.passwordHint')}
                    iconRight={
                      <PasswordVisibilityToggle
                        visible={showPassword}
                        onToggle={() => setShowPassword(v => !v)}
                        controlsId="existing-institution-password"
                      />
                    }
                  />
                  <FormField
                    id="existing-institution-confirm-password"
                    label={t('register.confirmPassword')}
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    required
                    disabled={loading}
                    iconRight={
                      <PasswordVisibilityToggle
                        visible={showConfirmPassword}
                        onToggle={() => setShowConfirmPassword(v => !v)}
                        controlsId="existing-institution-confirm-password"
                      />
                    }
                  />
                  <FormField
                    label={t('register.communicationLanguage')}
                    select
                    options={[
                      { value: CommunicationLanguage.English, label: t('register.languageEnglish') },
                      { value: CommunicationLanguage.Greek, label: t('register.languageGreek') },
                      { value: CommunicationLanguage.French, label: t('register.languageFrench') },
                      { value: CommunicationLanguage.Spanish, label: t('register.languageSpanish') },
                    ]}
                    value={communicationLanguage}
                    onChange={e =>
                      setCommunicationLanguage(e.target.value as CommunicationLanguage)
                    }
                    disabled={loading}
                  />
                  <CheckboxContainer>
                    <Checkbox
                      type="checkbox"
                      id="terms-institution"
                      checked={acceptedTerms}
                      onChange={e => setAcceptedTerms(e.target.checked)}
                      disabled={loading}
                    />
                    <CheckboxLabel htmlFor="terms-institution">
                      {t('register.acceptTerms')}
                    </CheckboxLabel>
                  </CheckboxContainer>
                </Stack>
              </FormSection>

              <FormButtonGroup direction="row" spacing="md" justify="space-between">
                <Button type="button" variant="outline" onClick={() => setStep(2)} disabled={loading}>
                  {t('common.back')}
                </Button>
                <Button
                  type="submit"
                  variant="gradient"
                  glow
                  ripple
                  loading={loading}
                  disabled={!acceptedTerms || !password || !confirmPassword || !email.trim()}
                >
                  {t('register.createAccount')}
                </Button>
              </FormButtonGroup>
            </Form>
          )}
        </Stack>
      </CardWithPadding>
    </AuthWizardShell>
  );
};

export default RegisterExistingInstitution;
